import { lazy } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router';
import Order from '@/pages/Order';
import My from '@/pages/My';
import MyAddress from '@/pages/Address/AddressList';
import SelectPlace from '@/pages/Address/SelectPlace';
import SearchPlace from '@/pages/Address/SearchPlace';
import SelectCity from '@/pages/Address/SelectCity';
import EditAddress from '@/pages/Address/EditAddress';
import CanteenSelect from '@/pages/Canteen/Select';
import CanteenSearch from '@/pages/Canteen/Search';
import CanteenDetail from '@/pages/Canteen/Detail';
import OrderConfirm from '@/pages/OrderConfirm';
import OrderPay from '@/pages/OrderPay';
import OrderDetail from '@/pages/Order/OrderDetail';
import OrderDetail2 from '@/pages/Order/OrderDetail/index2';
import UserInfo from '@/pages/My/UserInfo';
import Protocol from '@/pages/My/Protocol';
import Options from '@/pages/My/Options';
import OptionsPrivacy from '@/pages/My/Options/Privacy';
import Invoice from '@/pages/My/Invoice';
import Logout from '@/pages/My/Logout';
import CardList from '@/pages/CardList';
import CurCouponList from '@/pages/CardList/CurCouponList';
import CouponsDetail from '@/pages/CardList/CouponsDetail';
import CardDetail from '@/pages/CardList/CardDetail';
import EquityCardList from '@/pages/CardList/EquityCardList';
import { UserProfile } from '@/pages/UserProfile';
import { EnvChangePage } from '@/pages/ChangeEnv';

import Main from '@/components/Main/index.douyin';

import {
  MY_ADDRESS,
  SELECT_PLACE,
  SEARCH_PLACE,
  SELECT_CITY,
  EDIT_ADDRESS,
  HOME,
  MY,
  USER_INFO,
  CARD_LIST,
  PROTOCOL,
  OPTIONS,
  OPTIONS_PRIVACY,
  INVOICE,
  SELECT_CANTEEN,
  SEARCH_CANTEEN,
  CANTEEN_DETAIL,
  ORDER_CONFIRM,
  ORDER_PAY,
  LOGOUT,
  COUPONS_DETAIL,
  CARD_DETAIL,
  ORDER_DETAIL,
  AVAILABLE_CARD_LIST,
  LOGIN_UNIONPAY,
  ORDER_INVOICE_DETAIL,
  ORDER_LIST,
  TRANSIT_PAGE,
} from '@/constants/path';

import ProductPage from '@/pages/Product';
import ProductSearchPage from '@/pages/Product/Search/Page';
import ProductDetailPage from '@/pages/Product/Detail/Page';
import ProductCouponSelect from '@/pages/Product/Coupon/Select/Page';
import UnionpayLogin from '@/pages/UnionpayLogin';
import TransitPage from '@/pages/TransitPage';
import PrivacyPage from '@/pages/Privacy/Page';
import WelcomePendingPage from '@/pages/Welcome/Pending';
import AboutMCD from '@/pages/About/Page';
import CardListWithoutLoginPage from '@/pages/CardList/List';

import Redirect from './helpers/Redirect';

const OrderInvoiceDetail = lazy(() => import('@/pages/Order/InvoiceDetail'));
const MobileTestLogin = lazy(() => import('@/pages/MobileTestLogin/mobile_login'));

const routes: RouteObject[] = [
  {
    path: '/',
    children: [
      {
        index: true,
        element: <Navigate to={HOME} replace />,
      },
      {
        path: 'product',
        children: [
          {
            index: true,
            // [patch] 历史路由跳转转发, 并保留信息
            element: <Redirect to={HOME} keepSearch keepState replace />,
          },
          {
            path: 'search',
            element: <ProductSearchPage />,
          },
          {
            path: ':productCode',
            element: <ProductDetailPage />,
          },
          {
            path: 'coupon/select',
            element: <ProductCouponSelect />,
          },
        ],
      },
      {
        path: 'privacy',
        children: [
          {
            path: 'confirm',
            element: <PrivacyPage />,
          },
        ],
      },
      {
        path: 'welcome',
        children: [
          {
            path: 'pending',
            element: <WelcomePendingPage />,
          },
        ],
      },
      {
        path: 'about',
        children: [
          {
            path: 'mcd',
            element: <AboutMCD />,
          },
        ],
      },
    ],
  },
  {
    path: '/main',
    element: <Main />,
    children: [
      {
        path: HOME,
        element: (
          <ProductPage orderType={2} style={{ '--pp-bottom-gap': 'calc(50px + var(--safe-area-inset-bottom))' }} />
        ),
      },
      {
        path: MY,
        element: <My />,
      },
    ],
  },
  {
    path: '/login/mobile',
    element: <MobileTestLogin />,
  },
  {
    path: LOGIN_UNIONPAY,
    element: <UnionpayLogin />,
  },
  {
    path: '/user/profile',
    element: <UserProfile />,
  },
  {
    path: MY_ADDRESS,
    element: <MyAddress />,
  },
  {
    path: SELECT_PLACE,
    element: <SelectPlace />,
  },
  {
    path: SEARCH_PLACE,
    element: <SearchPlace />,
  },
  {
    path: SELECT_CITY,
    element: <SelectCity />,
  },
  {
    path: EDIT_ADDRESS,
    element: <EditAddress />,
  },
  {
    path: USER_INFO,
    element: <UserInfo />,
  },
  {
    path: PROTOCOL,
    element: <Protocol />,
  },
  {
    path: OPTIONS,
    element: <Options />,
  },
  {
    path: OPTIONS_PRIVACY,
    element: <OptionsPrivacy />,
  },
  {
    path: INVOICE,
    element: <Invoice />,
  },
  {
    path: LOGOUT,
    element: <Logout />,
  },
  {
    path: CARD_LIST,
    element: <CardList />,
  },
  {
    path: CARD_LIST,
    children: [
      {
        index: true,
        element: <CardList />,
      },
      {
        path: 'without_login',
        element: <CardListWithoutLoginPage />,
      },
    ],
  },
  {
    path: AVAILABLE_CARD_LIST,
    element: <CurCouponList />,
  },
  {
    path: COUPONS_DETAIL,
    element: <CouponsDetail />,
  },
  {
    path: CARD_DETAIL,
    element: <CardDetail />,
  },
  {
    path: '/cardList/equityCardList',
    element: <EquityCardList />,
  },
  {
    path: SELECT_CANTEEN,
    element: <CanteenSelect />,
  },
  {
    path: SEARCH_CANTEEN,
    element: <CanteenSearch />,
  },
  {
    path: CANTEEN_DETAIL,
    element: <CanteenDetail />,
  },
  {
    path: ORDER_CONFIRM,
    element: <OrderConfirm />,
  },
  {
    path: ORDER_PAY,
    element: <OrderPay />,
  },
  {
    path: ORDER_LIST,
    element: <Order />,
  },
  {
    path: ORDER_DETAIL,
    element: <OrderDetail />,
  },
  {
    path: ORDER_DETAIL + '2',
    element: <OrderDetail2 />,
  },
  {
    path: ORDER_INVOICE_DETAIL,
    element: <OrderInvoiceDetail />,
  },
  {
    path: 'change_env',
    element: <EnvChangePage />,
  },
  {
    path: TRANSIT_PAGE,
    element: <TransitPage />,
  },
  {
    path: '/*',
    element: <Navigate to="/main/home"></Navigate>,
  },
];

export default routes;
